/**
 * SoundFX — central registry + helpers for the City Builder sound effects.
 *
 * The raw files live in src/assets/GameAssets/New city building/ (imported as-is,
 * spaces and all — Phaser's loader and webpack copy handle the spaces fine, the
 * same way the EndScene art does). We map each to a clean key so the rest of the
 * code never has to repeat the file paths.
 *
 * Loading: call loadSoundEffects(scene) from LoadScene.preload/create.
 * Playback: playSfx(scene, SFX.x) for one-shots; startBgMusic/stopBgMusic for the
 * looping background track.
 *
 * Muting is handled globally by the UIScene sound toggle (game.sound.mute), so it
 * covers these effects automatically — no extra wiring needed here.
 */

export const SFX = {
  bgMusic: 'sfx-bg-music',
  click: 'sfx-click',
  correct: 'sfx-correct',
  error: 'sfx-error',
  appear: 'sfx-appear',
  pop: 'sfx-pop',
  popping: 'sfx-popping',
  zoom: 'sfx-zoom',
};

const SFX_BASE = 'assets/GameAssets/New city building';

const SFX_FILES = {
  [SFX.bgMusic]: 'friendly-town-fun-video-game-music-loop.mp3',
  [SFX.click]: 'click-retro.mp3',
  [SFX.correct]: 'correct answer.mp3',
  [SFX.error]: 'error.mp3',
  [SFX.appear]: 'appearance-effect.mp3',
  [SFX.pop]: 'pop.mp3',
  [SFX.popping]: 'popping.mp3',
  [SFX.zoom]: 'zooming in building.mp3',
};

// Per-effect playback volume so the chimes don't drown out the music (and the
// music sits quietly under the gameplay).
const SFX_VOLUME = {
  [SFX.bgMusic]: 0.1,
  [SFX.click]: 0.6,
  [SFX.correct]: 0.8,
  [SFX.error]: 0.7,
  [SFX.appear]: 0.6,
  [SFX.pop]: 0.7,
  [SFX.popping]: 0.7,
  [SFX.zoom]: 0.7,
};

// Queue every sound effect on the scene's loader. Safe to call once from LoadScene.
export function loadSoundEffects(scene) {
  Object.entries(SFX_FILES).forEach(([key, file]) => {
    scene.load.audio(key, `${SFX_BASE}/${file}`);
  });
}

// Fire-and-forget one-shot. Guards on the cache so a missing/late asset just
// no-ops instead of throwing.
export function playSfx(scene, key, config = {}) {
  if (!scene?.sound || !scene.cache.audio.exists(key)) return;
  scene.sound.play(key, { volume: SFX_VOLUME[key] ?? 1, ...config });
}

// Start (or resume) the looping background track. Stored on the global game so it
// survives scene restarts (replay) without ever stacking duplicate loops.
export function startBgMusic(scene) {
  if (!scene?.sound || !scene.cache.audio.exists(SFX.bgMusic)) return null;
  let music = scene.game.__bgMusic;
  if (!music) {
    music = scene.sound.add(SFX.bgMusic, {
      loop: true,
      volume: SFX_VOLUME[SFX.bgMusic],
    });
    scene.game.__bgMusic = music;
  }
  if (!music.isPlaying) music.play();
  return music;
}

export function stopBgMusic(scene) {
  scene?.game?.__bgMusic?.stop();
}
